import './movement-item-card.scss';

import { faCircleNotch, faUndo } from '@fortawesome/free-solid-svg-icons';
import {
    Item,
    itemSelectors,
    Movement,
    MovementDataStore,
    movementSelectors,
    MovementTypes,
} from '@skinsoft/entity';
import { ItemCard } from '@skinsoft/ui';
import { Component, Watch } from 'vue-property-decorator';

import { LinkableItemLifecycleStates } from '@skinsoft/entity-data-store';
import { ReactiveVue } from '@skinsoft/reactive-vue';
import { LocationName } from '../../../location/location-name';
import { ErrorRibbon } from './error-ribbon';
import { IdentifierInput } from './identifier-input';
import { LocationInput } from './location-input';

type State = 'wait-for-identifier' | 'wait-for-location' | 'validated';

@Component({
    components: {
        sErrorRibbon: ErrorRibbon,
        sIdentifierInput: IdentifierInput,
        sItemCard: ItemCard,
        sLocationInput: LocationInput,
        sLocationName: LocationName,
    },
    props: {
        commit: {
            required: true,
        },
        focused: {
            required: false,
        },
        item: {
            required: true,
        },
        movement: {
            required: true,
        },
        rollback: {
            required: true,
        },
    },

    template: require('./movement-item-card.html'),
})
export class MovementItemCard extends ReactiveVue {
    public item: Item;
    public movement: Movement;
    public interactive: boolean;
    public commit: () => void;
    public rollback: () => void;

    public $movementDataStore: MovementDataStore;
    public contentReady: boolean = false;
    public encounteredError: boolean = false;
    public undoIcon = faUndo;
    public circleNotchIcon = faCircleNotch;
    public reverting: boolean = false;
    public state: State = 'wait-for-identifier';

    get movementType() {
        return movementSelectors.movementType(this.movement);
    }

    get linkableItemState() {
        return movementSelectors.linkableItemState(this.movement, itemSelectors.id(this.item));
    }

    get realTimeLocation() {
        return movementSelectors.linkableItemArrivalLocation(
            this.movement,
            itemSelectors.id(this.item),
        );
    }

    get canBeReverted() {
        if (this.linkableItemState === LinkableItemLifecycleStates.SELECTED) {
            return false;
        }

        switch (this.movementType) {
            case MovementTypes.SIMPLE:
                return movementSelectors.canLinkableItemTransitionTo(
                    this.movement,
                    itemSelectors.id(this.item),
                    LinkableItemLifecycleStates.SELECTED,
                );

            case MovementTypes.COMPLEX:
            case MovementTypes.RETURN:
                return (
                    movementSelectors.canLinkableItemTransitionTo(
                        this.movement,
                        itemSelectors.id(this.item),
                        LinkableItemLifecycleStates.SELECTED,
                    ) ||
                    movementSelectors.canLinkableItemTransitionTo(
                        this.movement,
                        itemSelectors.id(this.item),
                        LinkableItemLifecycleStates.IN_TRANSIT,
                    )
                );
        }
    }

    public triggerError() {
        this.encounteredError = true;
    }

    public syncLinkableItemState() {
        switch (this.linkableItemState) {
            case LinkableItemLifecycleStates.SELECTED:
                this.state = 'wait-for-identifier';
                break;
            case LinkableItemLifecycleStates.IN_TRANSIT:
                if (this.movementType === MovementTypes.SIMPLE) {
                    this.state = 'validated';
                } else {
                    this.state = 'wait-for-location';
                }
                break;
            case LinkableItemLifecycleStates.FINALIZED:
                this.state = 'validated';
                break;
        }
    }

    public async revertItem() {
        this.waitForUserApproval(this.$t('movement.itemCard.revertPrompt'));

        this.reverting = true;

        const movementId = movementSelectors.id(this.movement);
        const linkableItemId = itemSelectors.id(this.item);
        try {
            switch (this.movementType) {
                case MovementTypes.SIMPLE:
                    await this.$movementDataStore.transitionLinkableItem(
                        movementId,
                        linkableItemId,
                        LinkableItemLifecycleStates.SELECTED,
                    );
                    break;
                case MovementTypes.COMPLEX:
                case MovementTypes.RETURN:
                    switch (this.linkableItemState) {
                        case LinkableItemLifecycleStates.IN_TRANSIT:
                            await this.$movementDataStore.transitionLinkableItem(
                                movementId,
                                linkableItemId,
                                LinkableItemLifecycleStates.SELECTED,
                            );
                            break;
                        case LinkableItemLifecycleStates.FINALIZED:
                            await this.$movementDataStore.transitionLinkableItem(
                                movementId,
                                linkableItemId,
                                LinkableItemLifecycleStates.IN_TRANSIT,
                            );
                            await this.$movementDataStore.transitionLinkableItem(
                                movementId,
                                linkableItemId,
                                LinkableItemLifecycleStates.SELECTED,
                            );
                            break;
                    }
                    break;
            }
            this.state = 'wait-for-identifier';
            this.$emit('change')
        } catch (error) {
            // tslint:disable-next-line:no-console
            console.error(error);
        } finally {
            this.reverting = false;
        }
    }

    @Watch('focused', { immediate: false })
    private onFocusedChange() {
        this.encounteredError = false;
    }

    @Watch('state', { immediate: false })
    private onStateChange(state: State) {
        // as this is not executing on the first render, we are sure this will happen only after a transition
        switch (state) {
            case 'wait-for-identifier':
                this.rollback();
                break;
            case 'validated':
                this.commit();
                break;
        }
    }

    @Watch('item', { immediate: true, deep: true })
    private onItemChange() {
        if (this.item) {
            this.syncLinkableItemState()
        }
    }
}
